// src/components/layout/NotificationBell.tsx
import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import axiosClient from "../api/axiosClient";
import { useNavigate } from "react-router-dom";

type ApiNotification = {
  notification_id: number;
  type: string;
  message: string;
  created_at: string;
  updated_at?: string;
  is_read: 0 | 1;
};

type ApiResponse = {
  status_filter?: "all" | "read" | "unread";
  total_notifications?: number;
  read_count?: number;
  unread_count?: number;
  filtered_count?: number;
  notifications: ApiNotification[];
};

const timeAgo = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff} sec${diff > 1 ? "s" : ""} ago`;
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m} min${m > 1 ? "s" : ""} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h > 1 ? "s" : ""} ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} day${d > 1 ? "s" : ""} ago`;
  return date.toISOString().split("T")[0];
};

const typeIcon = (type?: string) => {
  switch ((type || "").toLowerCase()) {
    case "profile":
      return "mdi:account-circle";
    case "payment":
    case "payment received":
      return "mdi:cash";
    case "document":
      return "mdi:file-upload";
    case "system":
    case "alert":
      return "mdi:alert-circle";
    default:
      return "mdi:bell-outline";
  }
};

const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"all" | "unread" | "read">("all");
  const [notifications, setNotifications] = useState<ApiNotification[]>([]);
  const [counts, setCounts] = useState<{
    total?: number;
    read?: number;
    unread?: number;
  }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const unreadBadge =
    typeof counts.unread === "number"
      ? counts.unread
      : notifications.filter((n) => n.is_read === 0).length;

  // unified fetch (list + counts)
  async function fetchNotifications(status: "all" | "unread" | "read" = "all") {
    setLoading(true);
    setError(null);
    try {
      const url =
        status === "all" ? "/notifications" : `/notifications?status=${status}`;
      const { data } = await axiosClient.get<ApiResponse>(url);
      setNotifications(data.notifications ?? []);
      setCounts({
        total: data.total_notifications,
        read: data.read_count,
        unread: data.unread_count,
      });
    } catch (e: any) {
      setError(e?.message ?? "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  // load counts on mount (so badge appears immediately)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axiosClient.get<ApiResponse>("/notifications");
        if (!mounted) return;
        setCounts({
          total: data.total_notifications,
          read: data.read_count,
          unread: data.unread_count,
        });
        // don't set full list here — wait until user opens or tab changes, to reduce initial payload
      } catch {
        // silent fallback; badge will use local list when available
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // fetch list when dropdown opens or tab changes
  useEffect(() => {
    if (open) fetchNotifications(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  // close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [open]);

  // mark single as read (optimistic)
  async function markAsRead(id: number) {
    const prev = notifications;
    setNotifications((p) =>
      p.map((n) => (n.notification_id === id ? { ...n, is_read: 1 } : n)),
    );
    setCounts((c) => ({
      ...c,
      unread:
        typeof c.unread === "number" ? Math.max(0, c.unread - 1) : undefined,
    }));

    try {
      await axiosClient.patch(`/notifications/${id}/read`);
    } catch (e: any) {
      setNotifications(prev);
      setError(e?.message ?? "Failed to mark read");
      fetchNotifications(tab);
    }
  }

  // mark all visible as read
  async function markAllVisible() {
    const unreadIds = notifications
      .filter((n) => n.is_read === 0)
      .map((n) => n.notification_id);
    if (!unreadIds.length) return;
    const prev = notifications;
    setNotifications((p) => p.map((n) => ({ ...n, is_read: 1 })));
    setCounts((c) => ({ ...c, unread: 0 }));

    try {
      await Promise.all(
        unreadIds.map((id) => axiosClient.patch(`/notifications/${id}/read`)),
      );
    } catch (e: any) {
      setNotifications(prev);
      setError(e?.message ?? "Failed to mark all");
      fetchNotifications(tab);
    }
  }

  useEffect(() => {
    function handlePointerDown(e: MouseEvent) {
      if (!ref.current) return;
      // composedPath is supported in modern browsers and handles shadow DOM / portals
      const path = (e as any).composedPath
        ? (e as any).composedPath()
        : (e as any).path;
      if (path && path.indexOf(ref.current) !== -1) return; // click inside -> ignore
      if (ref.current.contains(e.target as Node)) return; // fallback
      setOpen(false);
    }

    if (open) document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        aria-label="Open notifications"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-full hover:bg-slate-100 transition-colors focus:outline-none   light-bg-color text-blue-700 cursor-pointer"
      >
        <Icon icon="f7:bell" width="20" />
        {unreadBadge > 0 && (
          <span
            title={`${unreadBadge} unread`}
            className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white rounded-full bg-red-600 animate-pulse"
          >
            {unreadBadge > 99 ? "99+" : unreadBadge}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 md:right-5 mt-2 w-[92vw] md:w-96 light-bg-color rounded-xl shadow-xl border  border-slate-200 overflow-hidden z-50"
          style={{
            // keep a little gap from the top and bottom of the viewport on mobile
            maxHeight: "calc(100vh - 120px)",
            // better touch scrolling on iOS
            WebkitOverflowScrolling: "touch",
          }}
        >
          {" "}
          <div className="px-4 py-3 flex items-start justify-between border-b border-gray-600">
            <div>
              <h3 className="text-sm font-semibold text-color">
                Notifications
              </h3>
              <p className="text-xs text-slate-300">
                Recent activity for your account
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchNotifications(tab)}
                className="text-xs px-2 py-1 rounded-md hover:bg-slate-100 cursor-pointer bg-white"
                title="Refresh"
              >
                Refresh
              </button>
              {/* <button
                onClick={() => {
                  setOpen(false);
                  navigate("/notifications");
                }}
                className="text-xs text-blue-600 hover:underline"
              >
                Open page
              </button> */}
            </div>
          </div>
          {/* Tabs */}
          <div className="px-3 py-2 ">
            <div className="inline-flex rounded-lg bg-gray-50 p-1">
              {(["all", "unread", "read"] as const).map((t) => {
                const label =
                  t === "all"
                    ? `All${counts.total ? ` (${counts.total})` : ""}`
                    : t === "unread"
                      ? `Unread${counts.unread ? ` (${counts.unread})` : ""}`
                      : `Read${counts.read ? ` (${counts.read})` : ""}`;
                const active = tab === t;
                return (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`px-3 py-1 rounded-md text-xs font-medium ${active ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:bg-slate-100"}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <div className="mt-2 flex items-center justify-between">
              <div className="text-xs text-color">
                Showing{" "}
                <span className="font-medium">{notifications.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={markAllVisible}
                  className="text-xs px-2 py-1 rounded-md  hover:bg-slate-100 cursor-pointer bg-white"
                >
                  Mark all visible as read
                </button>
              </div>
            </div>
          </div>
          {/* List */}
          <div className="max-h-[calc(60vh-190px)] overflow-y-auto overflow-x-hidden scrollbar-thin-gray">
            {loading && (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-3 animate-pulse">
                    <div className="w-9 h-9 rounded-full bg-slate-100" />
                    <div className="flex-1">
                      <div className="h-3 bg-slate-100 rounded w-1/2 mb-2" />
                      <div className="h-2 bg-slate-100 rounded w-3/4 mb-2" />
                      <div className="h-2 bg-slate-100 rounded w-1/4" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && error && (
              <div className="p-4 text-sm text-red-500">Error: {error}</div>
            )}

            {!loading && !error && notifications.length === 0 && (
              <div className="p-4 text-sm text-slate-500">No notifications</div>
            )}

            {!loading &&
              !error &&
              notifications.map((n) => (
                <div
                  key={n.notification_id}
                  className={`flex items-start gap-1 md:gap-3 px-2 md:px-4 py-3 hover:bg-slate-50 border-b border-gray-200 ${n.is_read === 0 ? "light-bg-color" : "light-bg-color-inactive"}`}
                >
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-700">
                      <Icon icon={typeIcon(n.type)} width="18" />
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                          <span className="truncate text-color">{n.type}</span>
                          {n.is_read === 0 && (
                            <span className="ml-1 text-[11px] bg-red-600 text-white rounded px-1">
                              new
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-300 truncate">
                          {n.message}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          {timeAgo(n.created_at)}
                        </div>
                      </div>

                      <div className="flex-shrink-0 ml-3">
                        {/* inside the list rendering where you have the Mark as read button */}
                        {n.is_read === 0 ? (
                          <button
                            type="button" // <-- important
                            onClick={(e) => {
                              // <-- stop event from bubbling out
                              e.stopPropagation();
                              markAsRead(n.notification_id);
                            }}
                            className="text-xs px-2 py-1 rounded-md bg-gray-100 hover:bg-slate-200 cursor-pointer"
                          >
                            Mark as read
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">Read</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
          <div className="p-3 border-t border-gray-400 light-bg-color text-xs flex items-center justify-between">
            <div className="text-slate-200">Manage notifications</div>
            <div className="flex items-center gap-3">
              {/* <button onClick={() => fetchNotifications(tab)} className="text-xs hover:underline">
                Refresh
              </button> */}
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/notifications");
                }}
                className="text-xs text-blue-600 hover:underline cursor-pointer"
              >
                View all
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
